import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
	Box,
	Button,
	Stack,
	Step,
	StepConnector,
	StepContent,
	StepLabel,
	Stepper,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { cloneDeep, get, set } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIfUserAndOrganisationExist, RegisterUserAndOrganisation } from 'src/lib/api/AuthUser';
import GetRecords from 'src/lib/api/GetRecords';
import { APIS } from 'src/lib/constants/apis';
import { ANTRAGSTELLER_ORGANISATIONSTYP } from 'src/lib/constants/constant';
import { PAGES_URLS } from 'src/lib/constants/routes';
import { getFieldNamefromDataPath, isTrue, removeDataKeys, removeNullDeep } from 'src/utils/helper';
import ScrollToTop from '../../layout/ScrollToTop';
import LoadingBackdrop from '../../ui-component/loading/LoadingBackdrop';
import AbschlussRegistrierung from './abschluss/AbschlussAbteilungRegistrierung';
import AbteilungsDaten from './abteilung-daten/AbteilungsDaten';
import AbteilungsDatenValidator from './abteilung-daten/AbteilungsdatenValidator';
import ErfolgNachAbschluss from './erfolg/ErfolgNachAbschluss';
import NeueHochschuleDatenValidator from './hochschule-daten/NeueHochschuleDatenValidator';
import LoginDaten from './login-daten/LoginDaten';
import LoginDatenValidator from './login-daten/LoginDatenValidator';
import PersonDaten from './person-daten/PersonDaten';
import PersonDatenValidator from './person-daten/PersonDatenValidator';

// import schemas
import HochschuleSchema from 'src/lib/paths/hochschule_FULL_Paths.json';
import OrganisationsSchema from 'src/lib/paths/organisation_FULL_Paths.json';
import PersonSchema from 'src/lib/paths/person_FULL_Paths.json';
import UserSchema from 'src/lib/paths/user_Paths.json';
const U_P = UserSchema.attributes;
const O_P = OrganisationsSchema.attributes;
const P_P = PersonSchema.attributes;
const HS_P = HochschuleSchema.attributes;

const NEUE_HOCHSCHULE_ID = 'NEUE_HOCHSCHULE';

// Mapping für Hochschule_Forschungeinrichtung_* Felder
const mapNeueHochschuleToOrga = (zielObj: any, quelle: any) => {
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_Name.path, get(quelle, HS_P.Geschaeftsbezogene_Daten_Name_Der_Organisation.path, ''));
	set(zielObj, O_P.Geschaeftsbezogene_Daten_Kuerzel.path, get(quelle, HS_P.Geschaeftsbezogene_Daten_Kuerzel.path, ''));
	set(
		zielObj,
		O_P.Hochschule_Forschungeinrichtung_Rechtsform_Der_Organisation.path,
		get(quelle, HS_P.Geschaeftsbezogene_Daten_Rechtsform_Der_Organisation.path, ''),
	);
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_Steuernummer.path, get(quelle, HS_P.Unternehmensprofil_Steuernummer.path, ''));
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_U_St_Id_Nr.path, get(quelle, HS_P.Unternehmensprofil_U_St_Id_Nr.path, ''));
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_Strasse_Und_Hausnummer.path, get(quelle, HS_P.Adressdaten_Strasse_Und_Hausnummer.path, ''));
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_Adresszusatz.path, get(quelle, HS_P.Adressdaten_Adresszusatz.path, ''));
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_PLZ.path, get(quelle, HS_P.Adressdaten_PLZ.path, ''));
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_Ort.path, get(quelle, HS_P.Adressdaten_Ort.path, ''));
	set(
		zielObj,
		O_P.Hochschule_Forschungeinrichtung_Landkreis_Kreisfreie_Stadt.path,
		get(quelle, HS_P.Adressdaten_Landkreis_Kreisfreie_Stadt.path, ''),
	);
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_E_Mail.path, get(quelle, HS_P.Kontaktdaten_E_Mail.path, ''));
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_Telefon.path, get(quelle, HS_P.Kontaktdaten_Telefon.path, ''));
};

const clearHochschuleForschungsfelder = (zielObj: any) => {
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_Name.path, '');
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_Rechtsform_Der_Organisation.path, null);
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_Steuernummer.path, '');
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_U_St_Id_Nr.path, '');
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_Strasse_Und_Hausnummer.path, '');
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_Adresszusatz.path, '');
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_PLZ.path, '');
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_Ort.path, '');
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_Landkreis_Kreisfreie_Stadt.path, null);
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_E_Mail.path, '');
	set(zielObj, O_P.Hochschule_Forschungeinrichtung_Telefon.path, '');
};

const steps = [
	{
		label: 'Organisationsprofil',
		innerSteps: [],
	},
	{
		label: 'Ihr Profil',
		innerSteps: [],
	},
	{
		label: 'Ihre Zugangsdaten',
		innerSteps: [],
	},
	{
		label: 'Überprüfung',
		innerSteps: [],
	},
];

const AbteilungRegistrieren: FC<{}> = () => {
	const navigate = useNavigate();

	const [scrollToTop, setScrollToTop] = useState(false);
	const [activeStep, setActiveStep] = useState(0);

	// Webservice, component and response state
	const [loading, setLoading] = useState(false);
	const [wsSuccess, setWsSuccess] = useState<boolean | null>(null);
	const [formErrorMessage, setFormErrorMessage] = useState('');

	// Form Daten
	const [hochschulen, setHochschulen] = useState<any[]>([]);

	// Formvalues
	const [abteilungDaten, setAbteilungDaten] = useState({});
	const [neueHochschuleDaten, setNeueHochschuleDaten] = useState({});
	const [personDaten, setPersonDaten] = useState({});
	const [loginDaten, setLoginDaten] = useState({});
	const [datenschutzZustimmung, setDatenschutzZustimmung] = useState({
		zustimmung: false,
	});

	// Form Erros
	const [abteilungDatenError, setAbteilungDatenError] = useState({});
	const [personDatenError, setPersonDatenError] = useState({});
	const [loginDatenError, setLoginDatenError] = useState({});
	const [datenschutzZustimmungError, setDatenschutzZustimmungError] = useState({});
	const [neueHochschuleDatenError, setNeueHochschuleDatenError] = useState({});

	// Handle Update FormValues
	const handleUpdateAbteilungDaten = (key: string, value: any) => {
		let formValueCopy = cloneDeep(abteilungDaten);
		set(formValueCopy, key, value);

		if (key === O_P.Hochschule.path) {
			// Auswahl: Neue Hochschule
			if (value === NEUE_HOCHSCHULE_ID) {
				// Felder aus neueHochschuleDaten übernehmen
				mapNeueHochschuleToOrga(formValueCopy, neueHochschuleDaten);
				// Relation auf NEUE_HOCHSCHULE_ID (bleibt für UI)
				set(formValueCopy, O_P.Hochschule.path, NEUE_HOCHSCHULE_ID);
				// Adressdaten für Anzeige
				set(
					formValueCopy,
					O_P.Adressdaten_Strasse_Und_Hausnummer.path,
					get(neueHochschuleDaten, HS_P.Adressdaten_Strasse_Und_Hausnummer.path, ''),
				);
				set(formValueCopy, O_P.Adressdaten_Adresszusatz.path, get(neueHochschuleDaten, HS_P.Adressdaten_Adresszusatz.path, ''));
				set(formValueCopy, O_P.Adressdaten_PLZ.path, get(neueHochschuleDaten, HS_P.Adressdaten_PLZ.path, ''));
				set(formValueCopy, O_P.Adressdaten_Ort.path, get(neueHochschuleDaten, HS_P.Adressdaten_Ort.path, ''));
			} else {
				// Auswahl: Bestehende Hochschule
				const selectedHochschule = hochschulen.find((hochschule) => get(hochschule, 'id') === value);
				// Felder leeren
				clearHochschuleForschungsfelder(formValueCopy);
				// Relation setzen
				set(formValueCopy, O_P.Hochschule.path, value);
				// Adressdaten übernehmen
				set(
					formValueCopy,
					O_P.Adressdaten_Strasse_Und_Hausnummer.path,
					get(selectedHochschule, HS_P.Adressdaten_Strasse_Und_Hausnummer.path, ''),
				);
				set(formValueCopy, O_P.Adressdaten_Adresszusatz.path, get(selectedHochschule, HS_P.Adressdaten_Adresszusatz.path, ''));
				set(formValueCopy, O_P.Adressdaten_PLZ.path, get(selectedHochschule, HS_P.Adressdaten_PLZ.path, ''));
				set(formValueCopy, O_P.Adressdaten_Ort.path, get(selectedHochschule, HS_P.Adressdaten_Ort.path, ''));
				// KOntaktdaten übernehmen
				set(formValueCopy, O_P.Kontaktdaten_E_Mail.path, get(selectedHochschule, HS_P.Kontaktdaten_E_Mail.path, null));
				set(formValueCopy, O_P.Kontaktdaten_Telefon.path, get(selectedHochschule, HS_P.Kontaktdaten_Telefon.path, null));
				set(formValueCopy, O_P.Kontaktdaten_Fax.path, get(selectedHochschule, HS_P.Kontaktdaten_Fax.path, null));
				set(formValueCopy, O_P.Kontaktdaten_Webadresse.path, get(selectedHochschule, HS_P.Kontaktdaten_Webadresse.path, null));
				// Steuer-Informationen übernehmen
				set(formValueCopy, O_P.Unternehmensprofil_Steuernummer.path, get(selectedHochschule, HS_P.Unternehmensprofil_Steuernummer.path, ''));
				set(
					formValueCopy,
					O_P.Unternehmensprofil_Wirtschafts_Identifikationsnummer.path,
					get(selectedHochschule, HS_P.Unternehmensprofil_Wirtschafts_Identifikationsnummer.path, ''),
				);
				set(formValueCopy, O_P.Unternehmensprofil_U_St_Id_Nr.path, get(selectedHochschule, HS_P.Unternehmensprofil_U_St_Id_Nr.path, null));
				set(
					formValueCopy,
					O_P.Unternehmensprofil_Angaben_Zum_Vorsteuerabzug.path,
					get(selectedHochschule, HS_P.Unternehmensprofil_Angaben_Zum_Vorsteuerabzug.path, null),
				);
			}
		}

		console.log(' formValue copy ', formValueCopy);

		setAbteilungDaten(formValueCopy);
		let errorObject = cloneDeep(abteilungDatenError);
		delete errorObject[key];
		setAbteilungDatenError(errorObject);
		setScrollToTop(false);
	};
	const handleUpdatePersonDaten = (key: string, value: any) => {
		let formValueCopy = cloneDeep(personDaten);
		set(formValueCopy, key, value);
		setPersonDaten(formValueCopy);
		let errorObject = cloneDeep(personDatenError);
		delete errorObject[key];
		setPersonDatenError(errorObject);

		if (get(formValueCopy, P_P.Kontaktdaten_E_Mail.path, '')) {
			const contactEmail = get(formValueCopy, P_P.Kontaktdaten_E_Mail.path, '');
			setLoginDaten((prevValue: any) => ({
				...prevValue,
				[U_P.email.path]: contactEmail,
			}));
			setAbteilungDaten((prevValue: any) => {
				const updatedValue = cloneDeep(prevValue);
				const firstMailbenachrichtigungPath = `${O_P.Mailbenachrichtungen_Mail.path}.0.${getFieldNamefromDataPath(O_P.Mailbenachrichtungen_Mail.schema.attributes.Mailbenachrichtungen_Mail.path)}`;
				set(updatedValue, firstMailbenachrichtigungPath, contactEmail);
				return updatedValue;
			});
		}

		setScrollToTop(false);
	};
	const handleUpdateLoginDaten = (key: string, value: any) => {
		if (key === U_P.username.path && typeof value === 'string') value = value.trim();
		let formValueCopy = cloneDeep(loginDaten);
		set(formValueCopy, key, value);
		setLoginDaten(formValueCopy);
		let errorObject = cloneDeep(loginDatenError);
		delete errorObject[key];
		setLoginDatenError(errorObject);
		setFormErrorMessage('');
		setScrollToTop(false);
	};
	const handleUpdateDatenschutzZustimmung = (key: string, value: any) => {
		let formValueCopy = cloneDeep(datenschutzZustimmung);
		set(formValueCopy, key, value);
		setDatenschutzZustimmung(formValueCopy);

		let errorObject = cloneDeep(datenschutzZustimmungError);
		delete errorObject[key];
		setDatenschutzZustimmungError(errorObject);
		setScrollToTop(false);
	};
	const handleUpdateNeueHochschuleDaten = (key: string, value: any) => {
		let formValueCopy = cloneDeep(neueHochschuleDaten);
		set(formValueCopy, key, value);
		setNeueHochschuleDaten(formValueCopy);
		let errorObject = cloneDeep(neueHochschuleDatenError);
		delete errorObject[key];
		setNeueHochschuleDatenError(errorObject);
	};

	// Neue Hochschule im Modal "anlegen" (nur lokal, nicht in DB)
	const handleNeueHochschuleAnlegen = (): boolean => {
		let errorObject = {};
		let errs = NeueHochschuleDatenValidator.validate(neueHochschuleDaten);
		for (let error of errs) {
			set(errorObject, error.path, get(error, 'message'));
		}
		setNeueHochschuleDatenError(errorObject);
		if (Object.keys(errorObject).length > 0) return false;

		// Temporäre Hochschule mit Marker-ID in die Liste einfügen
		const neueHochschuleOption: any = { id: NEUE_HOCHSCHULE_ID };
		set(
			neueHochschuleOption,
			HS_P.Geschaeftsbezogene_Daten_angezeigter_Name.path,
			get(neueHochschuleDaten, HS_P.Geschaeftsbezogene_Daten_Name_Der_Organisation.path, 'Neue Hochschule'),
		);
		set(
			neueHochschuleOption,
			HS_P.Adressdaten_Strasse_Und_Hausnummer.path,
			get(neueHochschuleDaten, HS_P.Adressdaten_Strasse_Und_Hausnummer.path, ''),
		);
		set(neueHochschuleOption, HS_P.Adressdaten_Adresszusatz.path, get(neueHochschuleDaten, HS_P.Adressdaten_Adresszusatz.path, ''));
		set(neueHochschuleOption, HS_P.Adressdaten_PLZ.path, get(neueHochschuleDaten, HS_P.Adressdaten_PLZ.path, ''));
		set(neueHochschuleOption, HS_P.Adressdaten_Ort.path, get(neueHochschuleDaten, HS_P.Adressdaten_Ort.path, ''));

		// Alte temporäre Hochschule entfernen, falls vorhanden, und neue hinzufügen
		setHochschulen((prev: any) => [...prev.filter((h: any) => get(h, 'id') !== NEUE_HOCHSCHULE_ID), neueHochschuleOption]);

		// Abteilungsdaten aktualisieren: Hochschule auswählen + Adressdaten übernehmen
		let formValueCopy = cloneDeep(abteilungDaten);
		set(formValueCopy, O_P.Hochschule.path, NEUE_HOCHSCHULE_ID);
		set(formValueCopy, O_P.Adressdaten_Strasse_Und_Hausnummer.path, get(neueHochschuleDaten, HS_P.Adressdaten_Strasse_Und_Hausnummer.path, ''));
		set(formValueCopy, O_P.Adressdaten_Adresszusatz.path, get(neueHochschuleDaten, HS_P.Adressdaten_Adresszusatz.path, ''));
		set(formValueCopy, O_P.Adressdaten_PLZ.path, get(neueHochschuleDaten, HS_P.Adressdaten_PLZ.path, ''));
		set(formValueCopy, O_P.Adressdaten_Ort.path, get(neueHochschuleDaten, HS_P.Adressdaten_Ort.path, ''));
		mapNeueHochschuleToOrga(formValueCopy, neueHochschuleDaten);
		setAbteilungDaten(formValueCopy);

		return true;
	};

	// Validation Functions
	const validateAbteilungData = () => {
		let errorObject = {};
		let errs = AbteilungsDatenValidator(abteilungDaten).validate(abteilungDaten);
		for (let error of errs) {
			set(errorObject, error.path, get(error, 'message'));
		}
		setAbteilungDatenError(errorObject);
		return Object.keys(errorObject).length === 0;
	};
	const validatePersonData = () => {
		let errorObject = {};
		let errs = PersonDatenValidator.validate(personDaten);
		for (let error of errs) {
			set(errorObject, error.path, get(error, 'message'));
		}
		setPersonDatenError(errorObject);
		return Object.keys(errorObject).length === 0;
	};
	const validateLoginData = () => {
		let errorObject = {};
		let errs = LoginDatenValidator.validate(loginDaten);
		for (let error of errs) {
			set(errorObject, error.path, get(error, 'message'));
		}
		setLoginDatenError(errorObject);
		return Object.keys(errorObject).length === 0;
	};
	const validateDatenschutzZustimmung = () => {
		if (isTrue(datenschutzZustimmung.zustimmung)) {
			return true;
		}

		if (!isTrue(datenschutzZustimmung.zustimmung)) {
			let errorObject = {};
			set(errorObject, 'zustimmung', 'Sie müssen den Datenschutzhinweisen zum digitalen Antragsportal zustimmen.');
			setDatenschutzZustimmungError(errorObject);
			return false;
		}
	};

	// Handle Navigation
	const handleBack = () => {
		setScrollToTop(true);
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};
	const handleNext = async () => {
		setScrollToTop(true);
		switch (activeStep) {
			case 0:
				if (validateAbteilungData()) {
					setActiveStep((prev) => prev + 1);
				}
				break;
			case 1:
				if (validatePersonData()) {
					setActiveStep((prev) => prev + 1);
				}
				break;
			case 2:
				if (validateLoginData()) {
					if (get(loginDaten, U_P.password.path) !== get(loginDaten, U_P.repeatPassword.path)) {
						setLoginDatenError((prevValue) => ({
							...prevValue,
							[U_P.repeatPassword.path]: 'Beide Passwörter stimmen nicht überein.',
						}));
					} else {
						try {
							await CheckIfUserAndOrganisationExist(abteilungDaten, loginDaten);
							setActiveStep(3);
						} catch (err: any) {
							console.error('Error checking Perosn', err);
							setFormErrorMessage('Person existiert bereit');
						}
					}
				}
				break;
			default:
				break;
		}
	};

	// Submit Form
	const handleFormSubmit = async () => {
		if (validatePersonData() && validateAbteilungData() && validateDatenschutzZustimmung()) {
			const abteilungsDatenForSubmit = cloneDeep(abteilungDaten);

			set(
				abteilungsDatenForSubmit,
				O_P.Geschaeftsbezogene_Daten_Antragsteller_Organisationstyp.path,
				ANTRAGSTELLER_ORGANISATIONSTYP.AbteilungEinerOrganisation,
			);

			// Hochschule-Handling
			if (get(abteilungsDatenForSubmit, O_P.Hochschule.path) === NEUE_HOCHSCHULE_ID) {
				// Felder aus neueHochschuleDaten übernehmen
				mapNeueHochschuleToOrga(abteilungsDatenForSubmit, neueHochschuleDaten);
				// Relation für Backend auf null
				set(abteilungsDatenForSubmit, O_P.Hochschule.path, null);
			} else {
				// Bestehende Hochschule: Felder leeren
				clearHochschuleForschungsfelder(abteilungsDatenForSubmit);
			}

			try {
				setLoading(true);
				setScrollToTop(true);

				let res = await RegisterUserAndOrganisation(abteilungsDatenForSubmit, loginDaten, personDaten);
				if (res) {
					setWsSuccess(true);
					setActiveStep(4); // Move to step 4 on success
				} else {
					setFormErrorMessage('Sie müssen die Datenschutzhinweise zum digitalen Antragsportal zustimmen.');
					setActiveStep(3);
				}
			} catch (err: any) {
				console.error('ERROR: Creating Registrierung Anfrage ', err);
				setWsSuccess(false);
				setActiveStep(3);
			} finally {
				setLoading(false);
			}
		}
	};

	// Verhindert, die Seite zu verlassen, wenn der Benutzer mitten im Registrierungsprozess ist
	useEffect(() => {
		const unloadCallback = (event: any) => {
			event.preventDefault();
			event.returnValue = '';
			return '';
		};

		window.addEventListener('beforeunload', unloadCallback);
		return () => window.removeEventListener('beforeunload', unloadCallback);
	}, []);

	// Fetch Hochschulen for Mutterorganisation Dropdown
	useEffect(() => {
		const fetschHochschulen = async () => {
			try {
				const filter = {
					Im_Portal_In_Der_Auswahlliste_Anzeigen: true,
				};
				const res = await GetRecords(APIS.Hochschule, [], filter);

				if (res) {
					const resData = removeNullDeep(removeDataKeys(get(res, 'data', [])));
					setHochschulen(resData);
				}
			} catch (Err) {
				console.error('Error fetching Hochschulen: ', Err);
			}
		};

		fetschHochschulen();
	}, []);

	return (
		<>
			<LoadingBackdrop open={loading} />
			{scrollToTop && <ScrollToTop />}
			<Box
				sx={{
					display: 'flex',
					width: '100%',
					padding: '0 1.875rem',
					margin: '2rem auto 0',
					boxSizing: 'border-box',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: { xs: 'stretch', lg: 'baseline' },
						width: '100%',
						minHeight: '100px',
						flexDirection: { xs: 'column', lg: 'row' },
					}}
				>
					<Box
						sx={{
							textAlign: 'left',
							marginBottom: '2rem',
							display: activeStep === 4 ? 'none' : 'flex',
							flex: 1,
							flexDirection: 'column',
							alignItems: { xs: 'center', lg: 'flex-end' },
						}}
					>
						{!wsSuccess && (
							<Button
								variant='outlined'
								startIcon={<ArrowBackIcon />}
								onClick={() => navigate(PAGES_URLS.Login)}
								sx={{ m: '1rem 2rem 1rem 0', minWidth: '16.4rem' }}
							>
								Zurück zum Login
							</Button>
						)}
						<FormStepper activeStep={activeStep} />
					</Box>
					<Box sx={{ flex: '1 1 71.25rem', display: 'flex', justifyContent: activeStep === 4 ? 'center' : 'flex-start' }}>
						<Box
							sx={{
								width: '100%',
								maxWidth: '71.25rem',
								backgroundColor: '#fff',
								padding: '0 2rem 2rem',
							}}
						>
							{/* Step 1: Abteilungsdaten */}
							{activeStep === 0 && (
								<>
									<AbteilungsDaten
										formValue={abteilungDaten}
										updateFunction={handleUpdateAbteilungDaten}
										organisationDatenError={abteilungDatenError}
										hochschulen={hochschulen}
										neueHochschuleDaten={neueHochschuleDaten}
										updateNeueHochschuleFunction={handleUpdateNeueHochschuleDaten}
										neueHochschuleDatenError={neueHochschuleDatenError}
										onNeueHochschuleAnlegen={handleNeueHochschuleAnlegen}
									/>
								</>
							)}
							{/* Step 2: Personendaten */}
							{activeStep === 1 && (
								<>
									<PersonDaten formValue={personDaten} updateFunction={handleUpdatePersonDaten} personDatenError={personDatenError} />
								</>
							)}
							{/* Step 3: Login / Zugangsdaten */}
							{activeStep === 2 && (
								<>
									<LoginDaten
										formValue={loginDaten}
										updateFunction={handleUpdateLoginDaten}
										loginDatenError={loginDatenError}
										formErrorMessage={formErrorMessage}
									/>
								</>
							)}
							{/* Step 4: Abschluss &Datenschutz Zustimmung */}
							{activeStep === 3 && (
								<>
									<AbschlussRegistrierung
										formValue={datenschutzZustimmung}
										updateFunction={handleUpdateDatenschutzZustimmung}
										datenschutzZustimmungError={datenschutzZustimmungError}
										abteilungsDaten={abteilungDaten}
										personDaten={personDaten}
										loginDaten={loginDaten}
										alleHochschulen={hochschulen}
									/>
								</>
							)}

							{/* Step 5: Erfolg Meldung Seite */}
							{activeStep === 4 && <ErfolgNachAbschluss isRegistrationSuccess={wsSuccess} />}

							{/* * Logics for Navigation Buttons*/}
							<Box sx={{ mt: '3rem' }}>
								{activeStep === 3 ?
									<Stack direction='row' justifyContent='space-between'>
										<Button variant='outlined' onClick={handleBack}>
											Zurück
										</Button>
										<Button variant='contained' onClick={handleFormSubmit}>
											Jetzt registrieren
										</Button>
									</Stack>
								:	<React.Fragment>
										{activeStep < 3 && (
											<Stack direction={'row'} justifyContent={activeStep === 0 ? 'flex-end' : 'space-between'}>
												{activeStep > 0 && (
													<Button variant='outlined' disabled={activeStep === 0} onClick={handleBack}>
														Zurück
													</Button>
												)}
												<Button variant='contained' onClick={handleNext}>
													Weiter
												</Button>
											</Stack>
										)}
									</React.Fragment>
								}
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</>
	);
};

export default AbteilungRegistrieren;

// const formStrucOrga = (hochschulen: any[]) => {
// 	const hochSchulenOptions = getOptionsFromRes(hochschulen, getFieldNamefromDataPath(HS_P.Geschaeftsbezogene_Daten_Name_Der_Organisation.path));
// 	let struc: FormStrucItem[] = [
// 		{
// 			fields: [
// 				{ ...RA_P.Hochschule, type: FORM_FIELDS_TYPES.singleSelect, options: hochSchulenOptions },
// 				{ ...RA_P.Organisationsdaten_Name_Der_Abteilung, label: 'Fachbereich' },
// 				RA_P.Organisationsdaten_Strasse_Und_Hausnummer,
// 				RA_P.Organisationsdaten_Adresszusatz,
// 			],
// 			userProps: { direction: 'column', columnToDivide: 1 },
// 		},
// 		{
// 			fields: [RA_P.Organisationsdaten_PLZ, RA_P.Organisationsdaten_Ort],
// 			userProps: { columnToDivide: 2, columnGap: '1rem' },
// 		},
// 	];
// 	return struc;
// };
// const formStrucPerson = () => {
// 	let struc: FormStrucItem[] = [
// 		{
// 			fields: [RA_P.Nutzerdaten_Anrede, RA_P.Nutzerdaten_Titel],
// 			userProps: { columnToDivide: 2, columnGap: '1rem' },
// 		},
// 		{
// 			fields: [
// 				RA_P.Nutzerdaten_Vorname,
// 				RA_P.Nutzerdaten_Nachname,
// 				RA_P.Nutzerdaten_Benutzername,
// 				RA_P.Nutzerdaten_E_Mail,
// 				RA_P.Nutzerdaten_Telefon,
// 			],
// 			userProps: { direction: 'column', columnToDivide: 1 },
// 		},
// 	];
// 	return struc;
// };

const FormStepper: FC<{ activeStep: number }> = ({ activeStep }) => {
	const theme = useTheme();
	const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));

	return (
		<Box sx={{ minWidth: '16rem', margin: '1rem 2rem 1rem 0' }}>
			<Stepper
				alternativeLabel={!isLgUp}
				activeStep={activeStep}
				orientation={isLgUp ? 'vertical' : 'horizontal'}
				sx={{
					width: '100%',
					'& .MuiStepConnector-line': {
						borderColor: 'primary.main',
						borderWidth: '1.5px',
					},
				}}
			>
				{steps.map((ele: any, i: number) => {
					const stepLabel = (
						<StepLabel
							StepIconProps={{
								sx: {
									color: '#fff',
									'& .MuiStepIcon-text': {
										display: isLgUp ? 'none' : 'block',
										fill: '#023671',
										fontWeight: 'bold',
									},
									'&.MuiSvgIcon-root.Mui-active': {
										bgcolor: '#023671',
									},
									'&.MuiStepIcon-root.Mui-completed': {
										bgcolor: '#023671',
									},
									borderRadius: '50%',
									border: '1.5px solid #023671',
								},
							}}
						>
							{isLgUp && (
								<Typography
									variant='body2'
									sx={{
										fontFeatureSettings: "'liga' off, 'clig' off",
										fontFamily: '"Avenir Next LT Pro"',
										fontStyle: 'normal',
										fontSize: { md: '.8rem', lg: '0.875rem' },
									}}
								>
									{ele.label}
								</Typography>
							)}
						</StepLabel>
					);

					return (
						<Step key={i}>
							{isLgUp ?
								stepLabel
							:	<Tooltip title={ele.label} arrow>
									{stepLabel}
								</Tooltip>
							}
							<StepContent>
								<Box>
									<Stepper activeStep={i} orientation='vertical'>
										{ele.innerSteps.map((label: string, index: number) => {
											const stepProps = {};
											const labelProps = {};

											return (
												<Step key={index} {...stepProps}>
													<StepLabel {...labelProps}>
														<Typography fontSize={'.8rem'}>{label}</Typography>
													</StepLabel>
													<StepConnector
														sx={{
															display: 'none',
														}}
													></StepConnector>
												</Step>
											);
										})}
									</Stepper>
								</Box>
							</StepContent>
						</Step>
					);
				})}
			</Stepper>
		</Box>
	);
};
